/**
 * Der „Strang" eines Vorgangs — Notizen, Verlauf (Mails, Anrufe, Dokumente,
 * Termine), Live-Nachladen und alle Handler, die `ArbeitsstrangV4` braucht.
 *
 * Herausgelöst aus `pages/VorgangDetailPageV4.tsx` (Schadenmappe Stufe 2,
 * 13.10.2026), damit die Schadenmappe denselben Strang OHNE Vorgangskopf
 * rendern kann. Die Vorgangsseite benutzt den Hook selbst; was Seiten-UI ist
 * (Kopf-Entwurf, Speicher-Blitz, To-do-Anlage), hängt über `opts` daran.
 *
 * Verhaltensneutraler Umzug: Der Code unten ist der der Seite, nur die
 * Seiten-Callbacks laufen über `optsRef`, damit die Handler stabil bleiben.
 */
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { abonniereMailGesendet } from "../components/mail/versandBus";
import { useAuth } from "../context/AuthContext";
import { vorgangService, type TodoDto, type VorgangDto } from "../services/vorgangService";
import { vorgangNoteService, type AggregatedNoteDto } from "../services/vorgangNoteService";
import { mailLinkService, type MailLinkDto } from "../services/mailLinkService";
import { documentService, type DocumentDto } from "../services/documentService";
import { mailFileService, type ParsedMailFile } from "../services/mailFileService";
import { isMailFileLike } from "../components/documents/documentKind";
import { KATEGORIE_MAIL_ARCHIV } from "../components/vorgang/detail/terminNotizen";
import { KATEGORIE_NOTIZBILD, pruefeNotizbild } from "../components/notizbloecke/bildDarstellung";
import { ohneNotizbildDoppel } from "../components/vorgang/v4/notizbildDoppel";
import { telephonyService, type CallLogDto } from "../services/telephonyService";
import { calendarTerminService, type VorgangTerminDto } from "../services/calendarTerminService";
import { reinigeBlockHtml } from "../components/notizbloecke/blockHtml";
import type { AppUser } from "../services/userService";
import { canEditVorgang, kundeName, teamKontext } from "../utils/vorgangScope";
import { useUserLabels } from "./useUserLabels";
import { useVorgangNotesLive } from "./useVorgangNotesLive";
import { baueVerlauf, type VerlaufEintrag } from "../components/vorgang/v3/verlauf";
import { sortiereTodosNeuesteZuerst } from "../components/vorgang/v3/todoDarstellung";
import { kommentareAufVertreter, type Kommentar } from "../components/notizbloecke/kommentarModell";
import {
    gruppiereVorgangsMails,
    mailLinksDerGruppe,
    zuweisungsSchluessel,
} from "../components/vorgang/detail/vorgangMails";
import { nachladenBuendeln } from "../components/vorgang/v4/nachladenBuendeln";
import { fasseFassungenZusammen, fasseUnterhaltungenZusammen } from "../components/vorgang/v4/zusammenfassen";
import { useWindowManager } from "../components/windowManager/WindowManagerContext";
import { linkedMailService } from "../services/linkedMailService";
import { noteService } from "../services/noteService";
import { graphMailService } from "../services/graphMailService";
import {
    baueKommentarInhalt,
    type KommentarAnhang,
    type KommentarZusatz,
} from "../components/notizbloecke/kommentarInhalt";
import { speichereEreignisZuweisung } from "../components/vorgang/v4/ereignisZuweisung";
import { openLinkedMailWindow } from "../components/mail/LinkedMailWindow";
import { Vg3Kontextmenue, type MenuePunkt } from "../components/vorgang/v3/Vg3Kontextmenue";
import type { StrangHandlers } from "../components/vorgang/v4/ArbeitsstrangV4";
import { DokumentUmbenennenDialog } from "../components/vorgang/v4/DokumentUmbenennenDialog";
import {
    anhaengeMeldung,
    dokumentLoesenPatch,
    entfernenRueckfrage,
    loeschenRueckfrage,
    neueDokumentIds,
} from "../components/vorgang/v4/ereignisAktionen";
import { ereignisMenuePunkte } from "../components/vorgang/v4/ereignisMenue";
import { openComposeWindow } from "../components/mail/ComposeMailWindow";
import { mailAnhangName } from "../components/vorgang/detail/todoDrop";
import CallVorgangZuweisenDialog from "../components/telephony/CallVorgangZuweisenDialog";
import { weiseZu } from "../components/telephony/callVorgangZuweisen";
import { zuordnungenImVorgang } from "../components/telephony/callZuordnungen";
import { bestaetigen, hinweisen } from "../components/redesign/bestaetigen";
import { bilderFuerVorgang, MINDESTGROESSE_BILD } from "../components/mail/inlineBilder";
import { bilderAlsImportItems, frageBilderAuswahl } from "../components/mail/mailBilderUebernahme";
import { bildDateiname } from "../components/mail/inlineBilder";
import { bestaetigeNotizenLoeschen } from "../components/notizbloecke/notizenLoeschenFrage";

export interface VorgangStrangOptionen {
    /** Nach jedem erfolgreichen ladeVorgang — die Seite setzt daraus Betreff/Beschreibung des Kopfs. */
    onVorgangGeladen?: (vorgang: VorgangDto) => void;
    /**
     * Wird in ladeAlles mitgeladen (die Seite: verknüpfte To-dos). Darf nicht
     * rejecten (sonst rejected `ladeAlles`) — Fehler selbst abfangen.
     */
    zusatzLaden?: () => Promise<void>;
    /** Speicher-Rückmeldung (ersetzt flashSaved der Seite). */
    onGespeichert?: () => void;
    /**
     * „Neues To-do…" aus dem Rechtsklick-Menü; fehlt der Callback, gibt es den
     * Menüpunkt nicht. Bekommt den geladenen Vorgang mit — der Aufrufer muss
     * ihn nicht selbst aus dem Hook-Ergebnis herausgreifen.
     */
    onNeuesTodoFuer?: (eintrag: VerlaufEintrag, vorgang: VorgangDto) => void;
    /**
     * Zusätzliche Schreibsperre des Aufrufers (etwa die Schadenmappe ohne
     * Schreibrecht). Wird mit `canEditVorgang(…)` UND-verknüpft; fehlt sie,
     * gilt allein die Vorgangsregel.
     */
    canEdit?: boolean;
}

/** Handler ohne Wirkung — für `vorgangId === null` (kein Vorgang, nichts zu tun). */
const INERTE_HANDLER: StrangHandlers = {
    onOeffneMail: () => undefined,
    onNotizNeu: async () => undefined,
    onBlockAendern: async () => undefined,
    onBlockLoeschen: async () => undefined,
    onGeaendert: () => undefined,
};

export interface GemeinsameStrangProps {
    alleNotizen: AggregatedNoteDto[];
    users: AppUser[];
    canEdit: boolean;
    aktuellerUserId: string;
    vorgangId: number;
    contactId: number | null;
    contactName: string | null;
    vorgangNbr: string | null;
    vertragNr: string | null;
    risikoName: string | null;
    zugewieseneMessageIds: Set<string>;
    vorhandeneDokumentIds: ReadonlySet<number>;
    risiken: Array<{ id: number; label: string }>;
    vertraege: Array<{ id: number; label: string }>;
}

export interface VorgangStrang {
    vorgang: VorgangDto | null;
    setVorgang: React.Dispatch<React.SetStateAction<VorgangDto | null>>;
    loading: boolean;
    /**
     * Ist der Verlauf (Notizen, Mails, …) mindestens einmal geladen? `loading`
     * fällt schon nach dem Vorgang selbst — die Sprungmarke einer Mitteilung
     * (`?notiz=`) muss aber wissen, ob eine fehlende Notiz wirklich fehlt oder
     * nur noch nicht da ist.
     */
    verlaufGeladen: boolean;
    error: string | null;
    canEdit: boolean;
    currentUserId: string | null;
    users: GemeinsameStrangProps["users"];
    notizen: AggregatedNoteDto[];
    notizenAnzeige: AggregatedNoteDto[];
    mails: MailLinkDto[];
    mailGruppen: ReturnType<typeof gruppiereVorgangsMails>;
    anrufe: CallLogDto[];
    dokumente: DocumentDto[];
    termine: VorgangTerminDto[];
    mailDateien: Record<number, ParsedMailFile | null>;
    todos: TodoDto[];
    eintraege: VerlaufEintrag[];
    eigeneEintraege: VerlaufEintrag[];
    zugewieseneMessageIds: Set<string>;
    vorhandeneDokumentIds: ReadonlySet<number>;
    bezugRisiken: Array<{ id: number; label: string }>;
    bezugVertraege: Array<{ id: number; label: string }>;
    /** null, solange kein Vorgang geladen ist. */
    gemeinsameProps: GemeinsameStrangProps | null;
    ladeVorgang: () => Promise<void>;
    ladeNotizen: () => Promise<void>;
    ladeNotizenBald: () => void;
    ladeVerlauf: () => Promise<void>;
    ladeAlles: () => Promise<void>;
    /** Eine Zeile Rückmeldung am unteren Bildrand (gerendert über `overlays`). */
    zeigeHinweis: (text: string) => void;
    hinweis: string | null;
    handlers: StrangHandlers;
    /** Wie `handlers`, nur legt `onNotizNeu` einen Block der laufenden Notiz an. */
    laufendeHandlers: StrangHandlers;
    /**
     * Eine Datei für einen Kommentar bzw. eine Notiz aus dem Notiz-Sheet
     * hochladen — Bild als Notizbild am Vorgang, sonst als Dokument am Strang.
     */
    anhangHochladen: (datei: File, todoId: number | null) => Promise<KommentarAnhang>;
    speichereEintragZuweisung: (eintrag: VerlaufEintrag, todoId: number | null) => Promise<void>;
    /** Kontextmenü, Anruf-Zuordnen-Dialog, Umbenennen-Dialog, Hinweiszeile — einmal im Baum rendern. */
    overlays: React.ReactNode;
}

/**
 * @param vorgangId Der Vorgang des Strangs.
 *   - `null` = KEIN Vorgang (Schadenmappe ohne verknüpften Vorgang): der Hook
 *     ruht — `loading` ist `false`, `vorgang` und `gemeinsameProps` sind
 *     `null`, es wird nichts geladen, kein Live-Socket geöffnet, `canEdit` ist
 *     `false` und `handlers` sind wirkungslos.
 *   - eine nicht-endliche Zahl (NaN aus `Number(idStr)` der Seite) verhält
 *     sich wie bisher: `loading` bleibt `true`, es wird nichts geladen.
 * @param opts Seiten-Callbacks (über `optsRef` frisch gehalten) und
 *   `canEdit` als zusätzliche Schreibsperre (UND mit der Vorgangsregel).
 */
export function useVorgangStrang(vorgangId: number | null, opts: VorgangStrangOptionen = {}): VorgangStrang {
    const ruht = vorgangId === null;
    const id = vorgangId ?? Number.NaN;
    // Seiten-Callbacks per Ref — sonst baut jede Inline-Lambda der Seite die
    // Handler neu (und der ?mail=-Effekt der Seite liefe öfter).
    const optsRef = useRef(opts);
    optsRef.current = opts;

    const { user, isAdmin, isGfgl, teams } = useAuth();
    const currentUserId: string | null = user?.id ?? null;
    const { users } = useUserLabels();
    const { openWindow } = useWindowManager();

    const [vorgang, setVorgang] = useState<VorgangDto | null>(null);
    const [loading, setLoading] = useState(!ruht);
    const [error, setError] = useState<string | null>(null);

    const [notizen, setNotizen] = useState<AggregatedNoteDto[]>([]);
    const [verlaufGeladen, setVerlaufGeladen] = useState(false);
    const [mails, setMails] = useState<MailLinkDto[]>([]);
    const [anrufe, setAnrufe] = useState<CallLogDto[]>([]);
    const [dokumente, setDokumente] = useState<DocumentDto[]>([]);
    /**
     * Die Notizbilder des Vorgangs — nur, um eine Dokumentkarte mit DEMSELBEN
     * Inhalt auszublenden, wenn das Bild schon in einer Notiz steht
     * (`notizbildDoppel.ts`, User 23.10.2026). Selbst nie eine Karte.
     */
    const [notizbilder, setNotizbilder] = useState<DocumentDto[]>([]);
    /** Geparste lokale Maildateien; vorhandener Schlüssel = Darstellung als Mail. */
    const [mailDateien, setMailDateien] = useState<Record<number, ParsedMailFile | null>>({});
    const mailDateiLaufRef = React.useRef(0);
    const mailDateiCacheRef = React.useRef(new Map<number, ParsedMailFile>());
    /** Termine des Vorgangs mit Notiz-Seite (PO 14.09.2026) — Karten im Strom. */
    const [termine, setTermine] = useState<VorgangTerminDto[]>([]);

    /** Offenes Rechtsklick-Menü auf einer Ereigniskarte. */
    const [menue, setMenue] = useState<{ x: number; y: number; punkte: MenuePunkt[] } | null>(null);
    /** Dokument, das gerade umbenannt wird (Rechtsklick → „Umbenennen…“). */
    const [umbenennenDok, setUmbenennenDok] = useState<DocumentDto | null>(null);
    /** „Weiterem Vorgang zuordnen…" an einer Anrufkarte (PO 01.10.2026). */
    const [anrufZuordnen, setAnrufZuordnen] = useState<CallLogDto | null>(null);

    /** Eine Zeile Rückmeldung am unteren Bildrand; `null` = nichts zu sagen. */
    const [hinweis, setHinweis] = useState<string | null>(null);
    const hinweisTimer = React.useRef<number | null>(null);

    // Zur Renderzeit aus `opts.canEdit` (nicht über `optsRef`): `ereignisMenue`
    // und `gemeinsameProps` hängen an diesem Wert und müssen neu entstehen,
    // wenn er sich ändert.
    const canEdit =
        !ruht &&
        canEditVorgang(vorgang, currentUserId, isAdmin, isGfgl, teamKontext(teams)) &&
        (opts.canEdit ?? true);

    /**
     * Eine Rückmeldung, die man LESEN muss — „3 Anhänge übernommen, 1 war schon
     * da“ etwa (PO 16.09.2026).
     *
     * `flashSaved` reicht dafür nicht: das „gespeichert ✓“ steht im Kopf der
     * Seite, also weit über der Karte, auf der gerade geklickt wurde, und
     * verschwindet nach 1,5 Sekunden. Ein Hinweis-Popup wiederum hält die
     * Arbeit an — für eine Erfolgsmeldung zu viel. Deshalb eine Zeile unten am Bildrand: sichtbar, egal wie weit man
     * gescrollt hat, nach sechs Sekunden von selbst weg und ohne Mausfang
     * (`pointer-events: none`), damit sie nichts blockiert, was darunter liegt.
     * Fehler bleiben beim Hinweis-Popup (`hinweisen`) — die sollen aufhalten.
     */
    const zeigeHinweis = useCallback((text: string) => {
        if (hinweisTimer.current != null) window.clearTimeout(hinweisTimer.current);
        setHinweis(text);
        hinweisTimer.current = window.setTimeout(() => setHinweis(null), 6000);
    }, []);

    useEffect(
        () => () => {
            if (hinweisTimer.current != null) window.clearTimeout(hinweisTimer.current);
        },
        [],
    );

    /* ── Laden ───────────────────────────────────────────────────────────── */

    const ladeVorgang = useCallback(async () => {
        try {
            const v = await vorgangService.getVorgang(id, { force: true });
            setVorgang(v);
            optsRef.current.onVorgangGeladen?.(v);
            setError(null);
        } catch (err: any) {
            console.error("[VorgangDetailV4] Laden fehlgeschlagen", err);
            setError(err?.message ?? "Vorgang konnte nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    /**
     * NUR DIE NOTIZEN NEU LADEN (28.09.2026).
     *
     * Seit die Notizen fortlaufend gespeichert werden (spätestens alle vier
     * Sekunden, PO: „immer speichern … in Echtzeit bei den anderen
     * Bearbeitern"), löste jeder Autosave — der eigene wie über den Socket der
     * fremde — `ladeVerlauf` aus: Mails, Anrufe, Dokumente und Termine, die
     * Termine mit je einem Graph-Aufruf. Für eine geänderte Notiz reicht die
     * Notizliste.
     *
     * ÜBERHOLTE ANTWORTEN ZÄHLEN NICHT. Mehrere Ladevorgänge laufen
     * gleichzeitig (eigener Autosave, Socket-Echo, fremde Änderung); kam eine
     * ältere Antwort zuletzt an, stand im Feld kurz wieder der alte Text. Jeder
     * Lauf zieht eine Nummer, übernommen wird nur die jüngste.
     */
    const notizLaufRef = useRef(0);
    const ladeNotizen = useCallback(async () => {
        const lauf = ++notizLaufRef.current;
        try {
            const n = await vorgangNoteService.listAggregated(id);
            if (lauf === notizLaufRef.current) setNotizen(n);
        } catch (err) {
            console.error("[VorgangDetailV4] Notizen laden fehlgeschlagen", err);
        }
    }, [id]);
    /** Mehrere Live-Signale kurz hintereinander = EIN Nachladen. */
    const notizTimerRef = useRef<number | null>(null);
    const ladeNotizenBald = useCallback(() => {
        if (notizTimerRef.current != null) window.clearTimeout(notizTimerRef.current);
        notizTimerRef.current = window.setTimeout(() => {
            notizTimerRef.current = null;
            void ladeNotizen();
        }, 150);
    }, [ladeNotizen]);
    useEffect(
        () => () => {
            if (notizTimerRef.current != null) window.clearTimeout(notizTimerRef.current);
        },
        [],
    );

    /**
     * Dieselben vier Quellen wie in V3, plus die Termine (PO 14.09.2026).
     * Anrufe, Dokumente und Termine sind best-effort: ein Ausfall der
     * Telefonie oder von Graph darf den Schriftwechsel nicht mitreißen.
     */
    const ladeVerlauf = useCallback(async () => {
        const notizLauf = ++notizLaufRef.current;
        const [n, m, a, d, t, nb] = await Promise.all([
            vorgangNoteService.listAggregated(id).catch((err) => {
                console.error("[VorgangDetailV4] Notizen laden fehlgeschlagen", err);
                return [] as AggregatedNoteDto[];
            }),
            mailLinkService.listByVorgang(id).catch((err) => {
                console.error("[VorgangDetailV4] Mails laden fehlgeschlagen", err);
                return [] as MailLinkDto[];
            }),
            telephonyService
                .listCalls({ vorgangId: id, limit: 100 })
                .then((r) => r.calls)
                .catch(() => [] as CallLogDto[]),
            // Die abgelegte `.eml` einer zugeordneten Mail ist technisch ein Dokument
            // (Cluster ABLAGE, category 'E-Mail-Archiv') — im Notizbereich stünde
            // dieselbe Mail dann zweimal: als Mail-Block UND als Dokument-Block
            // (PO 14.09.2026: „emls werden im notiz block als dokument gespeichert,
            // darf nicht sein"; dieselbe Regel wie PO 31.08.2026 in `istAbgelegteMail`).
            // Hier am Ladepfad gefiltert, damit alle Ableitungen — Ereignis-Blöcke,
            // `#`-Bezugsauswahl, Archiv, Erst-To-do-Übernahme — auf einmal sauber sind.
            // Notizbilder (PO 14.09.2026) hält der Server selbst aus jeder
            // Liste heraus; hier steht die Kategorie trotzdem, damit am
            // Ladepfad lesbar ist, was im Strom NICHT als Karte erscheint.
            documentService
                .list({ vorgangId: id, excludeCategory: [KATEGORIE_MAIL_ARCHIV, KATEGORIE_NOTIZBILD] })
                .catch(() => [] as DocumentDto[]),
            // Die Termin-Notiz erscheint im Vorgang (PO 14.09.2026). `by-vorgang`
            // macht je Termin einen Graph-Roundtrip — darum best-effort wie die
            // Anrufe; bleibt Graph weg, fehlen nur die Termin-Karten.
            //
            // Das Notiz-HTML kommt vom Mail-Sanitizer (erlaubt Tabellen, Bilder,
            // `style`); hier geht es durch `reinigeBlockHtml` auf das Maß eines
            // Notizblocks — dieselbe Leitplanke, die jeder Block vor dem
            // Einsetzen passiert. Die Karte zeigt davon ohnehin nur den Klartext.
            calendarTerminService
                .listTermineByVorgang(id)
                .then((liste) =>
                    liste.map((x) =>
                        x.notizSeite?.contentHtml
                            ? {
                                  ...x,
                                  notizSeite: {
                                      ...x.notizSeite,
                                      contentHtml: reinigeBlockHtml(x.notizSeite.contentHtml),
                                  },
                              }
                            : x,
                    ),
                )
                .catch((err) => {
                    console.error("[VorgangDetailV4] Termine laden fehlgeschlagen", err);
                    return [] as VorgangTerminDto[];
                }),
            // Ausdrücklich nach der Kategorie gefragt — nur so liefert der
            // Server Notizbilder. Best-effort: fehlen sie, steht im
            // schlimmsten Fall eine doppelte Karte da.
            documentService
                .list({ vorgangId: id, category: KATEGORIE_NOTIZBILD })
                .catch(() => [] as DocumentDto[]),
        ]);
        // Eine inzwischen gestartete jüngere Notizabfrage gewinnt (siehe `ladeNotizen`).
        if (notizLauf === notizLaufRef.current) setNotizen(n);
        setMails(m);
        setAnrufe(a);
        setDokumente(d);
        setNotizbilder(nb);
        const lokaleMails = d.filter((dok) => isMailFileLike(dok.fileName, dok.mimeType));
        const mailDateiLauf = ++mailDateiLaufRef.current;
        // Schon vor dem Parser-Ergebnis fachlich als Mail zeichnen; `null`
        // liefert bei unlesbaren Altdateien einen sicheren Dateiname-Fallback.
        setMailDateien(Object.fromEntries(
            lokaleMails.map((dok) => [dok.id, mailDateiCacheRef.current.get(dok.id) ?? null]),
        ));
        // Höchstens vier Parser-/Storage-Aufrufe gleichzeitig; erfolgreiche
        // Ergebnisse bleiben über Reloads der Seite im Cache.
        void (async () => {
            const zuLaden = lokaleMails.filter((dok) => !mailDateiCacheRef.current.has(dok.id));
            for (let start = 0; start < zuLaden.length; start += 4) {
                await Promise.all(zuLaden.slice(start, start + 4).map(async (dok) => {
                    try {
                        mailDateiCacheRef.current.set(dok.id, await mailFileService.fetchParsed(dok.id));
                    } catch (err) {
                        console.error(`[VorgangDetailV4] Maildatei ${dok.id} lesen fehlgeschlagen`, err);
                    }
                }));
            }
            if (mailDateiLaufRef.current === mailDateiLauf) {
                setMailDateien(Object.fromEntries(
                    lokaleMails.map((dok) => [dok.id, mailDateiCacheRef.current.get(dok.id) ?? null]),
                ));
            }
        })();
        setTermine(t);
        setVerlaufGeladen(true);
    }, [id]);

    const ladeAlles = useCallback(async () => {
        await Promise.all([ladeVorgang(), ladeVerlauf(), optsRef.current.zusatzLaden?.()]);
    }, [ladeVorgang, ladeVerlauf]);

    useEffect(() => {
        if (ruht) {
            // Ohne Vorgang nichts laden; ein vorher geladener fällt weg.
            setVorgang(null);
            setError(null);
            setLoading(false);
            return;
        }
        if (!Number.isFinite(id)) return;
        setLoading(true);
        setVerlaufGeladen(false);
        void ladeAlles();
    }, [ruht, id, ladeAlles]);

    /*
     * T-49: Ein `onVorgangDirty` lädt die ganze Seite neu. Mehrere Signale
     * kurz hintereinander (Übernahme, Verschieben, Kolleginnen am selben
     * Vorgang) sind EIN Nachladen; läuft schon eines, wird höchstens ein
     * Folgelauf vorgemerkt. Der Bündler lebt je Vorgang und ruft immer die
     * aktuelle `ladeAlles` auf.
     */
    const ladeAllesRef = useRef(ladeAlles);
    ladeAllesRef.current = ladeAlles;
    const nachladenRef = useRef<ReturnType<typeof nachladenBuendeln> | null>(null);
    useEffect(() => {
        const b = nachladenBuendeln(() => ladeAllesRef.current());
        nachladenRef.current = b;
        return () => b.abbrechen();
    }, [id]);

    /*
     * EINE GESENDETE MAIL ERSCHEINT SOFORT (User 22.10.2026, Vorgang 44: „Ich
     * habe auf eine Mail geantwortet, und da ist die Mail nicht da, erst nach
     * dem Reload.“). Das Verfassen-Fenster meldet jeden Versand mit den
     * betroffenen Vorgängen — gilt er diesem, wird gebündelt nachgeladen (mit
     * einem gleichzeitigen `vorgang:dirty` vom Server zusammen EIN Lauf).
     */
    useEffect(
        () =>
            abonniereMailGesendet(({ vorgangIds }) => {
                if (vorgangIds.includes(id)) nachladenRef.current?.anstossen();
            }),
        [id],
    );

    const live = useVorgangNotesLive(vorgang?.id ?? null, {
        onCreated: ladeNotizenBald,
        onUpdated: ladeNotizenBald,
        onRemoved: ladeNotizenBald,
        onVorgangDirty: () => nachladenRef.current?.anstossen(),
    });
    // Unbekannt (etwa ohne Socket-Provider) heißt: nicht pollen.
    const liveVerbunden = live?.connected ?? true;

    /*
     * OHNE SOCKET TROTZDEM LIVE. Fällt die Verbindung aus (Proxy, Netzwechsel,
     * Chat-Dienst weg), käme keine fremde Notiz mehr an. Dann fragt die Seite
     * selbst nach — alle 10 s, nur solange sie sichtbar ist. Das Notizfeld
     * trägt ein, was sich geändert hat, und rührt nichts an, was hier
     * ungespeichert steht (`gleicheAb`).
     */
    useEffect(() => {
        if (liveVerbunden || !vorgang?.id) return;
        const t = window.setInterval(() => {
            if (document.visibilityState === "visible") void ladeNotizen();
        }, 10_000);
        return () => window.clearInterval(t);
    }, [liveVerbunden, vorgang?.id, ladeNotizen]);

    /* ── Ableitungen ─────────────────────────────────────────────────────── */

    /**
     * DIESELBE MAIL AUS ZWEI POSTFÄCHERN IST EINE KARTE (T-11, 28.09.2026).
     *
     * Hängt ein Thread aus `dc@` UND `cll@` am Vorgang, verknüpft der Auto-Link
     * jede Antwort je Postfach — gewollt (jeder behält seinen Vorgangs-Chip).
     * Angezeigt wird je Mail und Strang nur der Vertreter. Bewusst eine
     * ABLEITUNG über `mails` und nicht beim `setMails`: so zeigt der lokale
     * Stand nach Zuweisen/Entfernen dasselbe wie ein Neuladen.
     */
    const mailGruppen = useMemo(() => gruppiereVorgangsMails(mails), [mails]);

    /**
     * Die Notizen FÜR DIE ANZEIGE: Kommentare an einer ausgeblendeten
     * Postfachkopie hängen am Vertreter (sonst stünden sie als freier Block im
     * Strom). Geschrieben wird weiter mit `notizen` bzw. der Notiz-`id`.
     */
    const notizenAnzeige = useMemo(
        () => kommentareAufVertreter(notizen, mailGruppen.vertreterVon),
        [notizen, mailGruppen],
    );

    /**
     * EINE UNTERHALTUNG, EINE DATEI (Rücksprache 06.10.2026): Mails derselben
     * Unterhaltung und gleichnamige Dateien je Strang stehen als EINE Karte da,
     * die übrigen aufklappbar darin (`zusammenfassen.ts`). Was kommentiert ist,
     * bleibt eine eigene Karte — sonst stünde sein Faden unsichtbar in einer
     * fremden. Wie `mailGruppen` eine reine Ableitung.
     */
    const unterhaltungen = useMemo(() => {
        const kommentiert = new Set<number>();
        for (const n of notizenAnzeige) if (n.mailLinkId != null) kommentiert.add(n.mailLinkId);
        return fasseUnterhaltungenZusammen(mailGruppen.sichtbar, kommentiert);
    }, [mailGruppen, notizenAnzeige]);
    const fassungen = useMemo(() => {
        const kommentiert = new Set<number>();
        for (const n of notizen) if (n.documentId != null) kommentiert.add(n.documentId);
        // Ein Bild, das in einer Notiz steht, ist keine zweite Karte
        // (User 23.10.2026) — Bezug über den Inhalt, siehe `notizbildDoppel.ts`.
        const karten = ohneNotizbildDoppel(
            dokumente,
            notizbilder,
            notizen.map((n) => n.text),
            kommentiert,
        );
        return fasseFassungenZusammen(karten, {
            ausnehmen: new Set(Object.keys(mailDateien ?? {}).map(Number)),
            verankert: kommentiert,
        });
    }, [dokumente, notizbilder, notizen, mailDateien]);

    const eintraege: VerlaufEintrag[] = useMemo(
        () =>
            baueVerlauf({
                vorgangId: id,
                mails: unterhaltungen.sichtbar,
                unterhaltungVon: unterhaltungen.weitereVon,
                notizen,
                anrufe,
                dokumente: fassungen.sichtbar,
                fassungenVon: fassungen.weitereVon,
                termine,
                mailDateien,
            }),
        [unterhaltungen, fassungen, notizen, anrufe, termine, mailDateien],
    );

    // Bewusst auf der ROHLISTE: sonst böte der Picker die zweite
    // Postfachkopie einer schon verknüpften Mail wieder an.
    const zugewieseneMessageIds = useMemo(() => zuweisungsSchluessel(mails), [mails]);

    /**
     * Was schon im Strom liegt — Heimat wie Gast. Die Dokument-Auswahl graut
     * diese Zeilen aus („hängt hier schon"), statt sie erneut anzubieten.
     */
    const vorhandeneDokumentIds = useMemo(
        () => new Set(dokumente.map((d) => d.id)),
        [dokumente],
    );

    const todos: TodoDto[] = useMemo(
        () => sortiereTodosNeuesteZuerst(vorgang?.todos ?? []),
        [vorgang],
    );

    /** Die Einträge, die direkt am Vorgang hängen — sein eigener Strom. */
    const eigeneEintraege = useMemo(() => eintraege.filter((e) => e.todoId == null), [eintraege]);

    /**
     * Risiken und Verträge, auf die sich ein `#`-Bezug richten kann: die des
     * Vorgangs UND die seiner To-dos.
     */
    const bezugRisiken = useMemo(() => {
        if (!vorgang) return [];
        return Array.from(
            new Map(
                [vorgang, ...todos]
                    .flatMap((v) => v.risiken ?? (v.risiko ? [v.risiko] : []))
                    .map((r) => [r.id, { id: r.id, label: r.name || `Risiko ${r.id}` }] as const),
            ).values(),
        );
    }, [vorgang, todos]);
    const bezugVertraege = useMemo(() => {
        if (!vorgang) return [];
        return Array.from(
            new Map(
                [vorgang, ...todos]
                    .flatMap((v) => v.vertraege ?? (v.vertrag ? [v.vertrag] : []))
                    .map((v) => [v.id, { id: v.id, label: v.contractNbr || `Vertrag ${v.id}` }] as const),
            ).values(),
        );
    }, [vorgang, todos]);

    const gemeinsameProps = useMemo<GemeinsameStrangProps | null>(() => {
        if (!vorgang) return null;
        return {
            alleNotizen: notizenAnzeige,
            users,
            canEdit,
            aktuellerUserId: currentUserId ?? "",
            vorgangId: vorgang.id,
            contactId: vorgang.kunde?.id ?? null,
            contactName: vorgang.kunde ? kundeName(vorgang.kunde) : null,
            vorgangNbr: vorgang.vorgangNbr ?? null,
            vertragNr: vorgang.vertrag?.contractNbr ?? null,
            risikoName: vorgang.risiko?.name ?? null,
            zugewieseneMessageIds,
            vorhandeneDokumentIds,
            risiken: bezugRisiken,
            vertraege: bezugVertraege,
        };
    }, [
        vorgang,
        notizenAnzeige,
        users,
        canEdit,
        currentUserId,
        zugewieseneMessageIds,
        vorhandeneDokumentIds,
        bezugRisiken,
        bezugVertraege,
    ]);

    /* ── Handler ─────────────────────────────────────────────────────────── */

    /* ── Zuordnung eines Ereignisses an ein To-do ────────────────────────── */

    const speichereEintragZuweisung = useCallback(async (eintrag: VerlaufEintrag, todoId: number | null) => {
        // Eine Mail-Karte vertritt ggf. mehrere Postfachkopien (T-11): alle
        // wandern mit, sonst bliebe die ausgeblendete im alten Strang stehen.
        const mailLinkIds = eintrag.mail ? mailLinksDerGruppe(eintrag.mail.id, mailGruppen.gruppeVon) : undefined;
        await speichereEreignisZuweisung(eintrag, todoId, id, mailLinkIds);
    }, [id, mailGruppen]);

    const eintragZuweisen = useCallback(
        async (eintrag: VerlaufEintrag, todoId: number | null) => {
            try {
                await speichereEintragZuweisung(eintrag, todoId);
                await ladeVerlauf();
                optsRef.current.onGespeichert?.();
            } catch (err: any) {
                console.error("[VorgangDetailV4] Zuweisen fehlgeschlagen", err);
                void hinweisen({ titel: "Nicht zugewiesen", text: err?.message ?? "Zuweisen fehlgeschlagen.", ton: "danger" });
            }
        },
        [ladeVerlauf, speichereEintragZuweisung],
    );

    /**
     * „Aus dem Vorgang entfernen“ (PO 14.09.2026) — ENTFERNEN HEISST LÖSEN,
     * nie löschen: der Mail-Link fällt, die Mail bleibt in Graph; der Anruf
     * verliert Vorgangs- und To-do-Bezug, bleibt in der Telefonie; das
     * Dokument verliert beide Bezüge und wandert zum Kunden des Vorgangs
     * (`dokumentLoesenPatch` — sonst wäre es bezugslos und faktisch weg).
     *
     * Rückfrage davor (V3-Muster): ein Mail-Link ist hart gelöscht, kein
     * Papierkorb. Kommentare an einem gelösten Element bleiben stehen —
     * bewusster Randfall, kein Umbau.
     */
    const eintragLoesen = useCallback(
        async (eintrag: VerlaufEintrag) => {
            const kundeId = vorgang?.kunde?.id ?? null;
            const bleibtBeimKunden = (eintrag.dokument?.contactId ?? kundeId) != null;
            const gast = eintrag.dokument?.gast;
            const ok = await bestaetigen({
                titel: "Aus dem Vorgang entfernen?",
                text: entfernenRueckfrage(eintrag, {
                    bleibtBeimKunden,
                    ...(gast ? { heimat: gast.heimat } : {}),
                }),
                ja: "Entfernen",
                ton: "danger",
            });
            if (!ok) return;
            try {
                if (eintrag.mail) {
                    // Alle Postfachkopien der Karte lösen (T-11) — sonst käme
                    // die ausgeblendete nach dem Neuladen als eigene Karte
                    // zurück. Nacheinander: ein 403 bricht ab.
                    for (const lid of mailLinksDerGruppe(eintrag.mail.id, mailGruppen.gruppeVon)) {
                        await mailLinkService.remove(lid);
                    }
                }
                else if (eintrag.anruf) {
                    // Ein Anruf kann an mehreren Vorgängen/To-dos hängen
                    // (PO 01.10.2026): gelöst wird nur DIESE Zuordnung.
                    if (eintrag.zuordnungId != null) {
                        await telephonyService.removeZuordnung(eintrag.anruf.id, eintrag.zuordnungId);
                    } else {
                        await telephonyService.updateCall(eintrag.anruf.id, { vorgangId: null, todoId: null });
                    }
                } else if (eintrag.dokument) {
                    // Bei einem Gast-Dokument fällt nur die Verknüpfung. Der
                    // Lösen-Patch schriebe seine echten Bezüge um und risse es
                    // damit aus dem Vorgang, in dem es zu Hause ist.
                    if (gast) {
                        await vorgangService.loeseDokument(id, eintrag.dokument.id);
                    } else {
                        await documentService.update(
                            eintrag.dokument.id,
                            dokumentLoesenPatch(eintrag.dokument, kundeId),
                        );
                    }
                } else if (eintrag.termin) {
                    // Erst das To-do lösen, dann den Vorgang: So bleibt die
                    // P8-Invariante `todoId` gehört zu `vorgangId` auch im
                    // Zwischenstand erhalten. Der Graph-Termin selbst bleibt
                    // unangetastet; beide Aufrufe schreiben nur den Sidecar.
                    await calendarTerminService.setTodo(
                        eintrag.termin.mailbox,
                        eintrag.termin.graphEventId,
                        null,
                    );
                    await calendarTerminService.setVorgang(
                        eintrag.termin.mailbox,
                        eintrag.termin.graphEventId,
                        null,
                    );
                }
                await ladeVerlauf();
                optsRef.current.onGespeichert?.();
            } catch (err: any) {
                console.error("[VorgangDetailV4] Entfernen fehlgeschlagen", err);
                void hinweisen({ titel: "Nicht entfernt", text: err?.message ?? "Entfernen fehlgeschlagen.", ton: "danger" });
            }
        },
        [id, vorgang, ladeVerlauf, mailGruppen],
    );

    /**
     * Ein Dokument löschen (PO 15.09.2026) — im Unterschied zu `eintragLoesen`,
     * das nur die Verknüpfung zum Vorgang kappt.
     *
     * `DELETE /api/documents/:id` wird mit E2 (16.09.2026) zum PAPIERKORB:
     * gelöscht heißt weich markiert und wiederherstellbar, nicht aus dem
     * Speicher gelöscht. Die Oberfläche verspricht deshalb weder das eine noch
     * das andere — sie sagt, was in beiden Fällen stimmt: die Datei ist aus den
     * Listen verschwunden. Die namentliche Rückfrage bleibt, weil „Löschen“ und
     * „Aus dem Vorgang entfernen“ direkt untereinander stehen.
     */
    const dokumentLoeschen = useCallback(
        async (dok: NonNullable<VerlaufEintrag["dokument"]>) => {
            const ok = await bestaetigen({
                titel: "Dokument löschen?",
                text: loeschenRueckfrage(dok.fileName),
                ja: "In den Papierkorb",
                ton: "danger",
            });
            if (!ok) return;
            try {
                await documentService.remove(dok.id);
                await ladeVerlauf();
                zeigeHinweis(`„${dok.fileName}“ liegt jetzt im Papierkorb.`);
            } catch (err: any) {
                console.error("[VorgangDetailV4] Löschen fehlgeschlagen", err);
                void hinweisen({ titel: "Nicht gelöscht", text: err?.message ?? "Löschen fehlgeschlagen.", ton: "danger" });
            }
        },
        [ladeVerlauf, zeigeHinweis],
    );

    /**
     * „Anhänge übernehmen“ (PO 16.09.2026) — die Datei-Anhänge einer Mail als
     * Dokumente in den Vorgang legen.
     *
     * ══ WARUM DAS EIN EIGENER MENÜPUNKT IST ════════════════════════════════
     * Seit T-2 legt auch „An Bestehendes anhängen“ jede verknüpfte Mail samt
     * Anhängen beim Vorgangskunden ab (Ablage-Warteschlange, asynchron). Der
     * Menüpunkt bleibt für Altbestand aus der Zeit davor, für abgeschaltete
     * Übernahme (`MAIL_ARCHIVE_ATTACHMENTS`), für übersprungene Ablagen (zu
     * groß, verschlüsselt) und als sofortiger Nachzug von Hand. Der Endpoint
     * dedupt über eine Marke in `tags` — ein zweiter Klick legt nichts doppelt
     * an, sondern meldet „war schon da“.
     * ═══════════════════════════════════════════════════════════════════════
     *
     * Anders als der V3-Weg (`VorgangMailsTab`) bricht das hier NICHT ohne
     * Kunden ab: seit dem 14.09.2026 reicht dem Endpoint `vorgangId` als
     * Ablageziel — ein persönlicher Vorgang hat keinen Kunden, und genau dort
     * war der Punkt bisher nicht benutzbar.
     *
     * Die frisch angelegten Dokumente wandern anschließend an DAS TO-DO, an dem
     * die Mail hängt: der Import kennt nur `vorgangId`, und seit A4 gibt es
     * keinen Strang „am Vorgang“ mehr — ohne dieses Nachfassen stünden die
     * Anhänge in keiner Karte.
     */
    const anhaengeUebernehmen = useCallback(
        async (eintrag: VerlaufEintrag) => {
            const mail = eintrag.mail;
            if (!mail) return;
            try {
                const content = await linkedMailService.read(mail.id);
                if (content.source === "archive") {
                    const files = content.mail.attachments.filter(a => !a.isInline);
                    // T-236: Bilder aus dem Mailtext (ohne Logos) nur auf Nachfrage.
                    const bilder = content.mail.attachments.filter(
                        (a) => a.isInline && !!a.contentId && /^image\//i.test(a.contentType) && a.size >= MINDESTGROESSE_BILD,
                    );
                    // T-287: mit Vorschau und Einzelauswahl.
                    const gewaehlt = await frageBilderAuswahl(
                        bilder,
                        files.length,
                        "uebernehmen",
                        (b) => linkedMailService.downloadAttachment(mail.id, b.index),
                        (b) => bildDateiname(b.name, b.contentType, eintrag.at),
                        { groesse: (b) => b.size },
                    );
                    const alle = [...files, ...gewaehlt];
                    for (const file of alle) await linkedMailService.importAttachment(mail.id, file.index);
                    await ladeVerlauf();
                    zeigeHinweis(alle.length ? `${alle.length} Anhang/Anhänge übernommen.` : "Diese Mail hat keine Datei-Anhänge.");
                    return;
                }
                if (content.source === "unavailable") throw new Error(content.message);
                const anhaenge = await graphMailService.fetchAttachments(mail.mailbox, mail.messageId);
                const dateien = anhaenge.filter((a) => !a.isInline);
                // T-236: Bilder aus dem Mailtext (ohne Logos) nur auf Nachfrage,
                // mit sprechendem Namen statt `image001.png`.
                const bilder = await frageBilderAuswahl(
                    bilderFuerVorgang(anhaenge),
                    dateien.length,
                    "uebernehmen",
                    (b) => graphMailService.fetchAttachmentBlob(mail.mailbox, b.messageId ?? mail.messageId, b.id),
                    (b) => bildDateiname(b.name, b.contentType, eintrag.at),
                    { groesse: (b) => b.size },
                );
                const mitBildern = bilder.length > 0;
                if (dateien.length === 0 && !mitBildern) {
                    zeigeHinweis("Diese Mail hat keine Datei-Anhänge.");
                    return;
                }
                const summary = await graphMailService.importAttachments({
                    mailbox: mail.mailbox,
                    contactId: vorgang?.kunde?.id ?? undefined,
                    vorgangId: id,
                    // T-18: Liegt eine Datei schon in der Kundenakte, wird sie
                    // verknüpft statt kopiert — dann gleich an DIESES To-do.
                    todoId: eintrag.todoId ?? undefined,
                    attachments: [
                        ...dateien.map((a) => ({
                            messageId: a.messageId ?? mail.messageId,
                            attachmentId: a.id,
                            name: a.name,
                        })),
                        ...(mitBildern
                            ? bilderAlsImportItems(bilder, eintrag.at).map((b) => ({
                                  ...b,
                                  messageId: b.messageId ?? mail.messageId,
                              }))
                            : []),
                    ],
                });
                if (eintrag.todoId != null) {
                    for (const docId of neueDokumentIds(summary)) {
                        await documentService.update(docId, { todoId: eintrag.todoId });
                    }
                }
                await ladeVerlauf();
                zeigeHinweis(anhaengeMeldung(summary));
            } catch (err: any) {
                console.error("[VorgangDetailV4] Anhänge übernehmen fehlgeschlagen", err);
                void hinweisen({
                    titel: "Anhänge nicht übernommen",
                    text: err?.message ?? "Anhänge übernehmen fehlgeschlagen.",
                    ton: "danger",
                });
            }
        },
        [id, vorgang, ladeVerlauf, zeigeHinweis],
    );

    /**
     * Anruf ZUSÄTZLICH an ein To-do dieses Vorgangs (PO 01.10.2026) — die
     * Karte, auf der geklickt wurde, bleibt stehen. Kennt der Vorgang einen
     * Kontakt des Anrufs nicht, fragt danach das Kontakt-Popup (F2).
     */
    const anrufZusaetzlich = useCallback(
        async (eintrag: VerlaufEintrag, todoId: number) => {
            if (!eintrag.anruf) return;
            try {
                await weiseZu(eintrag.anruf, [{ vorgangId: id, todoId }]);
                await ladeVerlauf();
                optsRef.current.onGespeichert?.();
            } catch (err: any) {
                console.error("[VorgangDetailV4] Zusätzlich zuordnen fehlgeschlagen", err);
                void hinweisen({ titel: "Nicht zugeordnet", text: err?.message ?? "Zuordnen fehlgeschlagen.", ton: "danger" });
            }
        },
        [id, ladeVerlauf],
    );

    /**
     * „Als Anhang an neue Mail" (User 22.10.2026): öffnet das Verfassen-Fenster
     * wie „Mail schreiben" im Vorgang, mit der Mail als `.eml` im Anhang. Der
     * Server lädt sie erst beim Senden — mit derselben Leseregel wie beim
     * Öffnen im Vorgang.
     */
    const mailAlsAnhang = useCallback(
        (eintrag: VerlaufEintrag) => {
            const mail = eintrag.mail;
            if (!mail || !vorgang) return;
            const kundeId = vorgang.kunde?.id ?? null;
            openComposeWindow(openWindow, {
                vorgangId: id,
                ...(eintrag.todoId != null ? { todoId: eintrag.todoId } : {}),
                subject: `WG: ${mail.subject ?? ""}`.trim(),
                contactId: kundeId,
                empfaengerKontextId: kundeId,
                mailAnhaenge: [{ linkId: mail.id, name: mailAnhangName(mail.subject) }],
                onSent: () => {
                    void ladeVerlauf();
                    optsRef.current.onGespeichert?.();
                },
            });
        },
        [id, vorgang, openWindow, ladeVerlauf],
    );

    /**
     * Rechtsklick auf eine Ereigniskarte.
     *
     * WELCHE Punkte in welcher Reihenfolge erscheinen, entscheidet
     * {@link ereignisMenuePunkte} — eine reine Ableitung mit eigenem Test. Hier
     * bleibt nur das Anhängen der Handler: die Ordnung ist eine fachliche
     * Tabelle (PO 16.09.2026), das Auslösen die Sache der Seite.
     */
    const ereignisMenue = useCallback(
        (eintrag: VerlaufEintrag, ev: React.MouseEvent) => {
            if (!canEdit) return;
            ev.preventDefault();
            const anrufTodoIds = eintrag.anruf
                ? zuordnungenImVorgang(eintrag.anruf, id)
                      .map((z) => z.todoId)
                      .filter((t): t is number => t != null)
                : undefined;
            // Ohne `onNeuesTodoFuer` (etwa in der Schadenmappe) gibt es „Neues To-do…" nicht.
            const punkte: MenuePunkt[] = ereignisMenuePunkte(eintrag, todos, anrufTodoIds)
                .filter((p) => p.aktion?.art !== "neuesTodo" || optsRef.current.onNeuesTodoFuer)
                .map((p) => {
                    const rahmen = {
                        label: p.label,
                        ...(p.trennerDavor ? { trennerDavor: true } : {}),
                        ...(p.gefahr ? { gefahr: true } : {}),
                    };
                    if (p.aktion == null) return { ...rahmen, ueberschrift: true };
                    switch (p.aktion.art) {
                        case "anhaenge":
                            return { ...rahmen, onClick: () => void anhaengeUebernehmen(eintrag) };
                        case "alsAnhang":
                            return { ...rahmen, onClick: () => mailAlsAnhang(eintrag) };
                        case "umbenennen":
                            return {
                                ...rahmen,
                                onClick: () => eintrag.dokument && setUmbenennenDok(eintrag.dokument),
                            };
                        case "zuweisen": {
                            const todoId = p.aktion.todoId;
                            return { ...rahmen, onClick: () => void eintragZuweisen(eintrag, todoId) };
                        }
                        case "zusaetzlich": {
                            const todoId = p.aktion.todoId;
                            return { ...rahmen, onClick: () => void anrufZusaetzlich(eintrag, todoId) };
                        }
                        case "weitererVorgang":
                            return { ...rahmen, onClick: () => eintrag.anruf && setAnrufZuordnen(eintrag.anruf) };
                        case "neuesTodo":
                            // Das To-do anlegen ist Sache der Seite (Kasten, Entwurf);
                            // der Punkt steht nur da, wenn sie den Callback setzt.
                            return {
                                ...rahmen,
                                onClick: () => vorgang && optsRef.current.onNeuesTodoFuer?.(eintrag, vorgang),
                            };
                        case "entfernen":
                            return { ...rahmen, onClick: () => void eintragLoesen(eintrag) };
                        case "loeschen":
                            return {
                                ...rahmen,
                                onClick: () => eintrag.dokument && void dokumentLoeschen(eintrag.dokument),
                            };
                    }
                });
            setMenue({ x: ev.clientX, y: ev.clientY, punkte });
        },
        [
            canEdit,
            id,
            vorgang,
            todos,
            eintragZuweisen,
            anrufZusaetzlich,
            eintragLoesen,
            dokumentLoeschen,
            anhaengeUebernehmen,
            mailAlsAnhang,
        ],
    );

    /* ── Notiz-Handler ───────────────────────────────────────────────────── */

    const ergaenzeZuInformierende = useCallback(async (todoId: number | null | undefined, userIds: string[]) => {
        if (userIds.length === 0) return;
        if (todoId != null) {
            const todo = vorgang?.todos?.find((k) => k.id === todoId);
            const next = Array.from(new Set([...(todo?.zuInformierende ?? []), ...userIds]));
            await vorgangService.updateTodo(todoId, { zuInformierende: next });
        } else {
            const next = Array.from(new Set([...(vorgang?.zuInformierende ?? []), ...userIds]));
            await vorgangService.updateVorgang(id, { zuInformierende: next });
        }
        // Die Rollen werden aus `vorgang` gespeist, nicht aus dem Verlauf.
        await ladeVorgang();
    }, [id, vorgang, ladeVorgang]);

    /**
     * EIN ANHANG AM KOMMENTAR (PO 28.09.2026: „im Kommentar einen Anhang
     * hinzufügen, Bild, Dok, …").
     *
     *  · Ein BILD wird Notizbild — wie ein Bild im Notizfeld: Kategorie
     *    „Notizbild", aus allen Dokumentlisten heraus, im Kommentar gezeigt.
     *  · Alles andere wird ein gewöhnliches DOKUMENT des Strangs, an dem
     *    kommentiert wird (To-do, sonst Vorgang) — wie beim Ablegen per
     *    Ziehen (`DateiDropZoneV4`). Es steht damit auch im Verlauf und in der
     *    Akte; eine Police, die nur in einem Kommentar hinge, fände niemand.
     */
    const notizenRef = useRef(notizen);
    notizenRef.current = notizen;
    const kommentarAnhangHochladen = useCallback(
        async (datei: File, todoId: number | null): Promise<KommentarAnhang> => {
            if (pruefeNotizbild(datei) === null) {
                const doc = await documentService.upload({
                    file: datei,
                    vorgangId: id,
                    category: KATEGORIE_NOTIZBILD,
                });
                return { id: doc.id, name: datei.name, bild: true };
            }
            const doc = await documentService.upload(
                todoId != null ? { file: datei, todoId } : { file: datei, vorgangId: id },
            );
            return { id: doc.id, name: doc.fileName || datei.name, bild: false };
        },
        [id],
    );
    /** Der Strang eines vorhandenen Kommentars — wohin ein neuer Anhang beim Ändern gehört. */
    const strangVonKommentar = useCallback((k: Kommentar): number | null => {
        const n = notizenRef.current.find(
            (x) => x.id === k.id && (x.source ?? "vorgang") === k.quelle,
        );
        return n?.todoId ?? null;
    }, []);

    const handlers: StrangHandlers = useMemo(
        () => ({
            onOeffneMail: (link) => {
                openLinkedMailWindow(openWindow, link);
            },
            onNotizNeu: async (input, todoId, format, refs) => {
                const angelegt = await vorgangNoteService.create(id, {
                    text: input.text,
                    mentions: input.mentions,
                    todoId: todoId ?? null,
                    // Nur mitschicken, wenn es welche gibt — eine leere Liste
                    // löschte die gespeicherten Verweise.
                    ...(refs?.length ? { refs } : {}),
                    ...(format === "html" ? { format: "html" as const } : {}),
                });
                try {
                    await ergaenzeZuInformierende(todoId, input.zuInformieren);
                } catch (err) {
                    // Die Notiz ist bereits sicher gespeichert; ein erneuter
                    // Versuch erzeugte ein Duplikat.
                    console.error("[VorgangDetailV4] Zu informieren ergänzen fehlgeschlagen", err);
                }
                await ladeNotizen();
                // Der Schlüssel zurück ans Notizfeld: Es übernimmt den Text
                // damit AN ORT UND STELLE, statt ihn zu leeren und auf das
                // Nachladen zu warten — kein Flackern, kein Cursorsprung.
                // Eine neue Notiz ist immer eine `VorgangNote`.
                return { key: `vorgang:${angelegt.id}` };
            },
            onBlockAendern: async (block, html, refs, mentions) => {
                // Beide Dienste ERSETZEN die Erwähnungsliste. Ohne den Bestand
                // als Rückfall verlöre ein Block bei jeder Textänderung, wen er
                // erwähnt.
                const erwaehnt = mentions ?? block.mentions;
                if (block.source === "todo") {
                    // `format: "html"` MUSS mit (T-413): Das Feld schreibt
                    // Markup. Ohne die Angabe blieb eine ältere Klartext-Notiz
                    // als Klartext markiert — beim Laden standen die Tags dann
                    // als Zeichen da, und jedes weitere Ändern verpackte sie
                    // eine Schicht tiefer („<div>&lt;div&gt;…"). `refs` kennen
                    // To-do-Notizen nicht.
                    await noteService.update(block.id, { text: html, format: "html", mentions: erwaehnt });
                } else {
                    await vorgangNoteService.update(block.id, {
                        text: html,
                        format: "html",
                        ...(refs?.length ? { refs } : {}),
                        ...(erwaehnt.length ? { mentions: erwaehnt } : {}),
                    });
                }
                await ladeNotizen();
            },
            onBlockLoeschen: async (block) => {
                // Leergeräumt heißt gelöscht (PO 13.09.2026). Das Backend
                // riegelt den leeren Text ohnehin ab — hier wird die Löschung
                // ausdrücklich angefordert, damit der Block sofort verschwindet
                // und nicht erst beim nächsten Laden.
                if (block.source === "todo") await noteService.remove(block.id);
                else await vorgangNoteService.remove(block.id);
                await ladeNotizen();
            },
            /**
             * ══ WAS AM 20.09.2026 ENTFALLEN IST ═══════════════════════════
             * Mit dem EINEN Notizfeld gibt es keine Blöcke mehr, die sich
             * einzeln verschieben (`onBlockReihenfolge`), breiter ziehen
             * (`onBlockBreite`) oder per Rechtsklick einem anderen To-do
             * zuweisen ließen (`onBlockAktionen`). Das Hoch-Runter hat der PO
             * am 18.09.2026 gestrichen („brauchen wir nicht mehr"), das
             * Zuweisen am 20.09.2026 („nein, Text kann man ja kopieren").
             *
             * Der SERVERWEG für die Reihenfolge bleibt bestehen
             * (`vorgangNoteService.reorder`): Das Kundentagebuch verschiebt
             * weiter, und eine von Hand gesetzte Reihenfolge aus der Zeit
             * davor wird hier weiterhin respektiert.
             *
             * Die automatische Mitnahme der Vorgangsnotizen, wenn das erste
             * To-do entsteht, bleibt ebenfalls — sie ist kein Rechtsklick,
             * sondern die Regel „der Vorgang wird To-do 1"
             * (`uebernimmVorgangsstrang`).
             */
            bestaetigeLoeschen: bestaetigeNotizenLoeschen,
            onEreignisMenue: ereignisMenue,
            /**
             * EIN KOMMENTAR IST EINE NOTIZ MIT BEZUG (PO 13./14.09.2026).
             *
             * Keine neue Tabelle, kein neuer Endpoint, keine Migration: was
             * der Faden braucht, trägt `VorgangNote` längst — `parentId` an
             * einer Notiz, `mailLinkId` / `callLogId` / `documentId` an einem
             * Verlaufseintrag. Genau so legte es schon
             * `VorgangDetailPageV3.onEintragKommentar` an.
             *
             * `todoId` MUSS mit: ohne sie stünde der Kommentar am Vorgang
             * statt im Strang seines Elements und wäre dort unsichtbar,
             * sobald der Vorgang To-dos hat.
             */
            onKommentarNeu: async (ziel, text, mentions, anker, zusatz?: KommentarZusatz) => {
                // `#`-Bezüge und Anhänge (PO 28.09.2026). Ohne sie bleibt der
                // Kommentar Klartext wie bisher; mit ihnen wird er HTML, und
                // die Dateien werden ERST JETZT hochgeladen — ein abgebrochener
                // Kommentar hinterlässt kein Dokument.
                const inhalt = await baueKommentarInhalt(text, zusatz, (datei) =>
                    kommentarAnhangHochladen(datei, ziel.todoId ?? null),
                );
                if (ziel.art === "notiz" && ziel.quelle === "todo") {
                    // DIE GRENZE DES BESTANDS: `parentId` einer VorgangNote
                    // zeigt auf eine VorgangNote. Ein Kommentar an einer
                    // TodoNote muss deshalb in DIESELBE Tabelle — sonst hinge
                    // er an der gleichnamigen Id der falschen (beide Tabellen
                    // haben eigene Id-Folgen).
                    // Nicht erreichbar, solange `bloeckeAus` jede Notiz in
                    // ihrem eigenen Strang zeichnet — aber laut, falls doch:
                    // still nichts zu tun sähe aus wie gespeichert.
                    if (ziel.todoId == null) {
                        throw new Error(
                            "Diese Notiz gehört zu keinem To-do — sie lässt sich nicht kommentieren.",
                        );
                    }
                    await noteService.create(ziel.todoId, {
                        text: inhalt.text,
                        ...(inhalt.format === "html" ? { format: "html" as const } : {}),
                        ...(mentions.length ? { mentions } : {}),
                        parentId: ziel.id,
                        // Die markierte Textstelle (PO 18.09.2026); ohne sie
                        // gehört der Kommentar zur ganzen Notiz.
                        ...(anker ? { anker } : {}),
                    });
                } else {
                    await vorgangNoteService.create(id, {
                        text: inhalt.text,
                        ...(inhalt.format === "html" ? { format: "html" as const, refs: inhalt.refs } : {}),
                        ...(mentions.length ? { mentions } : {}),
                        todoId: ziel.todoId ?? null,
                        ...(ziel.art === "notiz" ? { parentId: ziel.id } : {}),
                        ...(ziel.art === "mail" ? { mailLinkId: ziel.id } : {}),
                        ...(ziel.art === "anruf" ? { callLogId: ziel.id } : {}),
                        ...(ziel.art === "dokument" ? { documentId: ziel.id } : {}),
                        ...(ziel.art === "termin" ? { terminId: ziel.id } : {}),
                        // Kommentar am Mail-Entwurf (User 22.10.2026) — wandert
                        // beim Senden an die gesendete Mail.
                        ...(ziel.art === "entwurf" && ziel.entwurfId ? { entwurfId: ziel.entwurfId } : {}),
                        ...(anker ? { anker } : {}),
                    });
                }
                await ladeVerlauf();
            },
            /**
             * EINEN EIGENEN KOMMENTAR KORRIGIEREN — UND ZURÜCKNEHMEN.
             *
             * Solange der Kommentar als gewöhnlicher Block im Strom stand,
             * konnte sein Autor ihn ändern und durch Leerräumen löschen. Hinter
             * der Sprechblase steht er nicht mehr im Strom — ohne diese beiden
             * Wege bliebe ein Tippfehler für immer in der Kundenakte.
             *
             * Die TABELLE entscheidet der Beitrag selbst (`quelle`): eine
             * `TodoNote` geht über `noteService`, eine `VorgangNote` über
             * `vorgangNoteService`. Beide Tabellen haben eigene Id-Folgen — der
             * falsche Dienst träfe die gleichnamige Zeile der anderen.
             *
             * `format: "text"` MUSS mit: der Faden schreibt Klartext
             * (`alsKlartext` beim Öffnen des Feldes). Bliebe ein alter Beitrag
             * auf `format: "html"` stehen, würde sein neuer Text als Markup
             * gelesen — ein `<` in „Betrag < 500" verschluckte den Rest der
             * Zeile. `noteService` kennt das Feld nicht; dort ist ohnehin alles
             * Klartext.
             *
             * Die Erwähnungen gehen IMMER mit, auch leer: sie werden bei jeder
             * Änderung neu aus dem vollen Text abgeleitet. Wer sein `@Kürzel`
             * herausnimmt, hat damit auch niemanden mehr erwähnt.
             */
            onKommentarAendern: async (k: Kommentar, text, mentions, zusatz?: KommentarZusatz) => {
                // Das Format geht IMMER mit — auch an der TodoNote: Ein
                // Kommentar mit Anhang, dem man den Anhang wieder nimmt, wird
                // wieder Klartext; bliebe er auf `html`, verschluckte ein `<`
                // im neuen Text den Rest der Zeile.
                const inhalt = await baueKommentarInhalt(text, zusatz, (datei) =>
                    kommentarAnhangHochladen(datei, strangVonKommentar(k)),
                );
                if (k.quelle === "todo") {
                    await noteService.update(k.id, { text: inhalt.text, format: inhalt.format, mentions });
                } else {
                    await vorgangNoteService.update(k.id, {
                        text: inhalt.text,
                        format: inhalt.format,
                        mentions,
                        ...(inhalt.format === "html" ? { refs: inhalt.refs } : {}),
                    });
                }
                await ladeVerlauf();
            },
            onKommentarLoeschen: async (k: Kommentar) => {
                if (k.quelle === "todo") await noteService.remove(k.id);
                else await vorgangNoteService.remove(k.id);
                await ladeVerlauf();
            },
            /**
             * DEN HAKEN AM KOMMENTAR SETZEN (PO 18.09.2026: „Der kriegt die
             * Info und kann dann sagen: alles klar, erledigt.").
             *
             * Eigener Weg statt `onKommentarAendern`: Das Ändern bleibt dem
             * Autor vorbehalten, das Abhaken steht jedem offen, der den Vorgang
             * sieht (PO 20.09.2026: „erstmal jeder"). Die TABELLE entscheidet
             * wie überall der Beitrag selbst (`quelle`) — beide haben eigene
             * Id-Folgen, der falsche Dienst träfe die gleichnamige Zeile der
             * anderen.
             */
            onKommentarErledigt: async (k: Kommentar, erledigt: boolean) => {
                if (k.quelle === "todo") await noteService.setErledigt(k.id, erledigt);
                else await vorgangNoteService.setErledigt(k.id, erledigt);
                await ladeNotizen();
            },
            /**
             * EIN BILD IST EIN DOKUMENT (PO 14.09.2026: „bilder in die notiz
             * laden"). Kein Base64 im Notiztext — jeder Autosave und jeder
             * Live-Frame trüge sonst Megabytes. Das Bild geht in den
             * S3-Speicher wie jedes Dokument, mit `category: 'Notizbild'`;
             * der Server hält diese Kategorie aus allen Dokumentlisten heraus
             * (sonst würde aus jedem Bild eine Dokument-Karte im Strom). In
             * der Notiz steht nur `<img data-bild-id>`; den Rest macht
             * `bildDarstellung.ts`. Immer an den VORGANG, nicht ans To-do:
             * das Bild gehört zum Text, und der weiß selbst, in welchem
             * Strang er steht.
             */
            onBildHochladen: async (file: File) => {
                const doc = await documentService.upload({
                    file,
                    vorgangId: id,
                    category: KATEGORIE_NOTIZBILD,
                });
                return { id: doc.id };
            },
            onGeaendert: () => ladeAlles(),
        }),
        [
            id,
            openWindow,
            ladeVerlauf,
            ladeNotizen,
            ladeAlles,
            ergaenzeZuInformierende,
            ereignisMenue,
            kommentarAnhangHochladen,
            strangVonKommentar,
        ],
    );

    /**
     * Dieselben Handgriffe für die LAUFENDE NOTIZ eines Vorgangs mit To-dos
     * (User 01.11.2026): Nur das Anlegen unterscheidet sich — der neue Block
     * trägt `laufend`, damit ihn die Übernahme ins erste To-do stehen lässt.
     * Ändern, Löschen und Kommentieren laufen über die gewöhnlichen Wege; ein
     * Kommentar erbt das Kennzeichen serverseitig von seiner Notiz.
     */
    const laufendeHandlers: StrangHandlers = useMemo(
        () => ({
            ...handlers,
            onNotizNeu: async (input, _todoId, format, refs) => {
                const angelegt = await vorgangNoteService.create(id, {
                    text: input.text,
                    mentions: input.mentions,
                    todoId: null,
                    laufend: true,
                    ...(refs?.length ? { refs } : {}),
                    ...(format === "html" ? { format: "html" as const } : {}),
                });
                try {
                    await ergaenzeZuInformierende(undefined, input.zuInformieren);
                } catch (err) {
                    console.error("[VorgangDetailV4] Zu informieren ergänzen fehlgeschlagen", err);
                }
                await ladeNotizen();
                return { key: `vorgang:${angelegt.id}` };
            },
        }),
        [handlers, id, ergaenzeZuInformierende, ladeNotizen],
    );

    /* ── Rechtsklick-Overlays ─────────────────────────────────────────────── */

    // Die drei Rechtsklick-Overlays und die Hinweiszeile gehören zum Strang,
    // nicht zur Seite — so bekommt jede Seite mit Strang das Menü mit.
    const overlays = (
        <>
            {menue && (
                <Vg3Kontextmenue
                    x={menue.x}
                    y={menue.y}
                    punkte={menue.punkte}
                    onClose={() => setMenue(null)}
                />
            )}

            {anrufZuordnen && (
                <CallVorgangZuweisenDialog
                    call={anrufZuordnen}
                    onClose={() => setAnrufZuordnen(null)}
                    onAssigned={() => {
                        void ladeVerlauf().then(() => optsRef.current.onGespeichert?.());
                    }}
                />
            )}

            {umbenennenDok && (
                <DokumentUmbenennenDialog
                    dokument={umbenennenDok}
                    onGespeichert={async () => {
                        await ladeVerlauf();
                        optsRef.current.onGespeichert?.();
                    }}
                    onSchliessen={() => setUmbenennenDok(null)}
                />
            )}

            {/* Die Rückmeldung zu einer Menü-Aktion — unten am Bildrand,
                weil die Karte, auf der geklickt wurde, überall stehen kann.
                `role="status"` liest sie Screenreadern vor, ohne den
                Fokus zu nehmen. */}
            {hinweis && (
                <div className="vg4-hinweis" role="status">
                    {hinweis}
                </div>
            )}
        </>
    );

    return {
        vorgang, setVorgang, loading, verlaufGeladen, error, canEdit, currentUserId, users,
        notizen, notizenAnzeige, mails, mailGruppen, anrufe, dokumente, termine, mailDateien,
        todos, eintraege, eigeneEintraege, zugewieseneMessageIds, vorhandeneDokumentIds,
        bezugRisiken, bezugVertraege, gemeinsameProps,
        ladeVorgang, ladeNotizen, ladeNotizenBald, ladeVerlauf, ladeAlles,
        zeigeHinweis, hinweis,
        handlers: ruht ? INERTE_HANDLER : handlers,
        laufendeHandlers: ruht ? INERTE_HANDLER : laufendeHandlers,
        anhangHochladen: kommentarAnhangHochladen,
        speichereEintragZuweisung, overlays,
    };
}
